import { mapGetters } from 'vuex';
import { DateTime } from 'luxon';
import { EstablishmentSetting } from '@/enums';
import { GtmVariablesEnum } from '@/mixins/ga-enum';

export const analyticsEventHandler = {
    computed: {
        ...mapGetters({
            getSelectedService: 'services/getSelectedService',
            getPlaces: 'places/getPlaces',
            getPlaceById: 'places/getPlaceById',
            getSelectedPlaceId: 'places/getSelectedPlaceId',
            getSelectedPlacePanel: 'places/getSelectedPlacePanel',
            upperLocale: 'upperLocale',
            getDisableGoogleAnalyticsSetting: 'establishment/getDisableGoogleAnalyticsSetting',
            getServiceHasDataCollectionFormId: 'services/getServiceHasDataCollectionFormId',
            getClientFiltersIsAdmissible: 'services/getClientFiltersIsAdmissible',
            getEstablishment: 'establishment/getEstablishment',
        }),
        defaultParams() {
            return {
                [GtmVariablesEnum.SERVICE_TEMPLATE_ID]: this.getSelectedService.serviceTemplate.id,
                [GtmVariablesEnum.SERVICE_TEMPLATE_NAME_FR]: this.getSelectedService.serviceTemplate.descriptionFr,
                [GtmVariablesEnum.ESTABLISHMENT_SERVICE_TEMPLATE_ID]: this.getSelectedService.id,
                [GtmVariablesEnum.ESTABLISHMENT_SERVICE_TEMPLATE_NAME_FR]: this.getSelectedService.nameFr,
                [GtmVariablesEnum.ESTABLISHMENT_ID]: this.getEstablishment.id,
                [GtmVariablesEnum.ESTABLISHMENT_NAME]: this.getEstablishment.name,
                [GtmVariablesEnum.HEAD_OFFICE_REGION_CODE]: this.getEstablishment.headOffice.regionCode,
            };
        }
    },
    methods: {
        publicAvailabilitySelectedContinueButton(date, time) {
            try {
                if (date && time && !this.getDisableGoogleAnalyticsSetting) {
                    this.$gtm.push({
                        'event': 'publicAvailabilitySelectedButton',
                        [GtmVariablesEnum.IS_DATA_COLLECTION_ENABLED]: !!this.getServiceHasDataCollectionFormId,
                        [GtmVariablesEnum.APPOINTMENT_DATE_TIME]: date + ' ' + time,
                    });
                }
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicSelectAvailabilityDate(date) {
            try {
                if (date && !this.getDisableGoogleAnalyticsSetting) {
                    let place = { name: '', postalCode: '', region: '' };
                    if (this.getPlaceById[this.getSelectedPlaceId]) {
                        place.name = this.getPlaceById[this.getSelectedPlaceId].nameFr;
                        place.postalCode = this.getPlaceById[this.getSelectedPlaceId].postalCode;
                        place.region = this.getPlaceById[this.getSelectedPlaceId].region;
                    }
                    else if (this.getPlaces[this.getSelectedPlacePanel]) {
                        place.name = this.getPlaces[this.getSelectedPlacePanel].nameFr;
                        place.postalCode = this.getPlaces[this.getSelectedPlacePanel].postalCode;
                        place.region = this.getPlaces[this.getSelectedPlacePanel].region;
                    }
                    this.$gtm.push({
                        ...this.defaultParams,
                        event: 'publicSelectAvailabilityDate',
                        [GtmVariablesEnum.APPOINTMENT_DATE]: date,
                        [GtmVariablesEnum.ESTABLISHMENT_TYPE]: this.getEstablishmentSettingById(EstablishmentSetting.ESTABLISHMENT_TYPE),
                        [GtmVariablesEnum.NB_OF_DAYS_BEFORE_APPT]: DateTime.fromISO(date).diff(DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })).as('days'),
                        [GtmVariablesEnum.PLACE_NAME]: place.name,
                        [GtmVariablesEnum.PLACE_POSTAL_CODE]: place.postalCode,
                        [GtmVariablesEnum.PLACE_REGION]: place.region,
                        [GtmVariablesEnum.IS_DATA_COLLECTION_ENABLED]: !!this.getServiceHasDataCollectionFormId,
                    });
                }
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicSelectServiceAnalytics() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    event: 'publicSelectService',
                    [GtmVariablesEnum.ESTABLISHMENT_SERVICE_TEMPLATE_NAME_FR]: this.getSelectedService['nameFr'],
                    [GtmVariablesEnum.ESTABLISHMENT_SERVICE_TEMPLATE_ID]: this.getSelectedService.id,
                    [GtmVariablesEnum.IS_DATA_COLLECTION_ENABLED]: !!this.getServiceHasDataCollectionFormId,
                });
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicSelectPlaceAnalytics(index) {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    event: 'publicSelectPlace',
                    [GtmVariablesEnum.PLACE_NAME]: this.getPlaces[index].nameFr,
                    [GtmVariablesEnum.IS_DATA_COLLECTION_ENABLED]: !!this.getServiceHasDataCollectionFormId,
                });
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicTakeApptAnalytics(apptResponse) {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                const appointmentDate = DateTime.fromISO(apptResponse[0].start).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                const appointmentCreatedDate = DateTime.fromISO(apptResponse[0].created).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                this.$gtm.push({
                    ...this.defaultParams,
                    [GtmVariablesEnum.APPOINTMENT_NUMBER]: apptResponse[0].appointmentNumber,
                    [GtmVariablesEnum.APPOINTMENT_ID]: apptResponse[0].id,
                    [GtmVariablesEnum.ESTABLISHMENT_TYPE]: this.getEstablishmentSettingById(EstablishmentSetting.ESTABLISHMENT_TYPE),
                    [GtmVariablesEnum.NB_OF_DAYS_BEFORE_APPT]: appointmentDate.diff(appointmentCreatedDate).as('days'),
                    [GtmVariablesEnum.PLACE_NAME]: this.getPlaceById[apptResponse[0].place].nameFr,
                    [GtmVariablesEnum.PLACE_POSTAL_CODE]: this.getPlaceById[apptResponse[0].place].postalCode,
                    [GtmVariablesEnum.PLACE_REGION]: this.getPlaceById[apptResponse[0].place].region,
                    [GtmVariablesEnum.IS_DATA_COLLECTION_ENABLED]: !!this.getServiceHasDataCollectionFormId,
                    event: 'publicApptTaken',
                });
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicQuestionFilterAnalytics() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    ...this.defaultParams,
                    event: 'publicQuestionFilter',
                    [GtmVariablesEnum.IS_FILTER_ADMISSIBLE]: this.getClientFiltersIsAdmissible,
                    [GtmVariablesEnum.IS_DATA_COLLECTION_ENABLED]: !!this.getServiceHasDataCollectionFormId,
                });
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicIsDataCollectionFormCompletedAnalytics() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    ...this.defaultParams,
                    event: 'publicIsDataCollectionFormCompleted',
                    [GtmVariablesEnum.IS_DATA_COLLECTION_FORM_COMPLETED]: true,
                });
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicIsDataCollectionFormTokenTimeout() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    ...this.defaultParams,
                    event: 'publicIsDataCollectionFormTokenTimeout',
                    [GtmVariablesEnum.IS_DATA_COLLECTION_FORM_TOKEN_TIMEOUT]: true,
                });
            }
            catch (e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },
        publicMobileFooterMenuLoginAnalytics() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    event: 'publicMobileFooterMenuLogin'
                });
            }
            catch(e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },

        publicMobileFooterMenuAccountAnalytics() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    event: 'publicMobileFooterMenuAccount'
                });
            }
            catch(e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },

        publicMobileFooterMenuTakeApptAnalytics() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    event: 'publicMobileFooterMenuTakeAppt'
                });
            }
            catch(e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },

        publicMobileFooterMenuCancelApptAnalytics() {
            if (this.getDisableGoogleAnalyticsSetting) return;
            try {
                this.$gtm.push({
                    event: 'publicMobileFooterMenuCancelAppt'
                });
            }
            catch(e) {
                console.warn('Google Tags Manager instance not initiated');
                console.error(e);
            }
        },

    },
};
